import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { API } from "../lib/api";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE"]);
const LS_KEY = "ld_tr_hi";

// Persistent en -> hi cache
const loadCache = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
};
const cache = loadCache();
const saveCache = () => { try { localStorage.setItem(LS_KEY, JSON.stringify(cache)); } catch {} };

const hasLetters = (s) => /[A-Za-z]/.test(s);

function shouldSkip(el) {
  while (el) {
    if (el.nodeType === 1) {
      if (SKIP_TAGS.has(el.tagName)) return true;
      if (el.hasAttribute && el.hasAttribute("data-no-translate")) return true;
    }
    el = el.parentElement;
  }
  return false;
}

function collectTextNodes(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const txt = node.nodeValue;
      if (!txt || !txt.trim() || !hasLetters(txt)) return NodeFilter.FILTER_REJECT;
      if (shouldSkip(node.parentElement)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const out = [];
  let n;
  while ((n = walker.nextNode())) out.push(n);
  return out;
}

function collectPlaceholders(root) {
  return [...root.querySelectorAll("input[placeholder], textarea[placeholder]")]
    .filter((el) => el.placeholder && el.placeholder.trim() && hasLetters(el.placeholder) && !shouldSkip(el));
}

export const PageTranslator = () => {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const originals = useRef(new WeakMap());
  const touched = useRef([]);
  const observer = useRef(null);
  const running = useRef(false);

  useEffect(() => {
    const root = document.querySelector(".App");
    if (!root) return;

    const applyMap = (nodes, phNodes) => {
      observer.current && observer.current.disconnect();
      nodes.forEach((node) => {
        const orig = originals.current.get(node);
        const key = orig.trim();
        const tr = cache[key];
        if (tr) node.nodeValue = orig.replace(key, tr);
      });
      phNodes.forEach((el) => {
        const orig = originals.current.get(el);
        const tr = cache[(orig || "").trim()];
        if (tr) el.placeholder = tr;
      });
      startObserver();
    };

    const restore = () => {
      observer.current && observer.current.disconnect();
      touched.current.forEach((node) => {
        if (!originals.current.has(node)) return;
        if (node.nodeType === 3) node.nodeValue = originals.current.get(node);
        else node.placeholder = originals.current.get(node);
      });
    };

    const translate = async () => {
      if (running.current) return;
      running.current = true;
      try {
        const nodes = collectTextNodes(root);
        const phNodes = collectPlaceholders(root);
        const need = new Set();
        nodes.forEach((node) => {
          if (!originals.current.has(node)) originals.current.set(node, node.nodeValue);
          touched.current.push(node);
          const key = originals.current.get(node).trim();
          if (!cache[key]) need.add(key);
        });
        phNodes.forEach((el) => {
          if (!originals.current.has(el)) originals.current.set(el, el.placeholder);
          touched.current.push(el);
          const key = el.placeholder.trim();
          if (!cache[key]) need.add(key);
        });

        applyMap(nodes, phNodes); // apply whatever is already cached immediately

        const list = [...need];
        for (let i = 0; i < list.length; i += 40) {
          const chunk = list.slice(i, i + 40);
          try {
            const res = await fetch(`${API}/translate`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ texts: chunk, target: "hi" }),
            });
            const data = await res.json();
            Object.assign(cache, data.translations || {});
          } catch { /* keep english */ }
        }
        saveCache();
        applyMap(nodes, phNodes);
      } finally {
        running.current = false;
      }
    };

    const startObserver = () => {
      if (lang !== "hi") return;
      observer.current && observer.current.disconnect();
      observer.current = new MutationObserver(() => {
        clearTimeout(startObserver._t);
        startObserver._t = setTimeout(translate, 350);
      });
      observer.current.observe(root, { childList: true, subtree: true, characterData: true });
    };

    if (lang === "hi") {
      const t = setTimeout(translate, 120);
      return () => { clearTimeout(t); observer.current && observer.current.disconnect(); };
    } else {
      observer.current && observer.current.disconnect();
      restore();
    }
  }, [lang, pathname]);

  return null;
};
